/**
 * RETIRED REFERENCES (Tom's ruling D, 2026-10-18; payload VC-D R4): a
 * holder's pins whose target was retired. The holder's open entry carries
 * `retired_pins: [{pin, island, type, name}]` (absent: none), as served to
 * THIS reader — a retired island the reader cannot read is never listed.
 *
 * Kept per holder as of its latest open, and only under the reader's access
 * as it was then (VC-Authz, 2026-10-18): an access change hides every list
 * until the next open answers it again. Nothing else feeds it — no retire
 * event, no announce, no other reader's answer.
 */

import { useSyncExternalStore } from "react";
import { reviewAccessEpoch, useReviewAccessEpoch } from "../review-access";

export type RetiredPin = { pin: string; island: string; type?: string; name?: string };

const byHolder = new Map<string, { epoch: number; list: RetiredPin[] }>();
const listeners = new Set<() => void>();
const NONE: RetiredPin[] = [];

const notify = () => { for (const l of Array.from(listeners)) l(); };
const str = (v: unknown): v is string => typeof v === "string" && v.length > 0;

/** What the holder's latest open answered (an absent list: none retired). */
export function setRetiredPins(holder: string, raw: unknown): void {
  const list: RetiredPin[] = Array.isArray(raw)
    ? raw.filter((r) => !!r && typeof r === "object" && str(r.pin) && str(r.island)).map((r) => ({
        pin: r.pin, island: r.island,
        ...(str(r.type) ? { type: r.type } : {}),
        ...(str(r.name) ? { name: r.name } : {}),
      }))
    : NONE;
  if (!list.length && !byHolder.has(holder)) return;
  if (list.length) byHolder.set(holder, { epoch: reviewAccessEpoch(), list });
  else byHolder.delete(holder);
  notify();
}

export function forgetRetiredPins(holder: string): void {
  if (byHolder.delete(holder)) notify();
}

export function retiredPinsOf(holder: string | null | undefined): RetiredPin[] {
  const held = holder ? byHolder.get(holder) : undefined;
  return held && held.epoch === reviewAccessEpoch() ? held.list : NONE;
}

export function useRetiredPins(holder: string | null | undefined): RetiredPin[] {
  useReviewAccessEpoch(); // an access change re-renders, and hides what was answered before it
  return useSyncExternalStore(
    (l) => { listeners.add(l); return () => { listeners.delete(l); }; },
    () => retiredPinsOf(holder),
    () => NONE,
  );
}

export function resetRetiredPinsForTests(): void {
  byHolder.clear();
  notify();
}
